import { useEffect, useRef, useState } from 'react';
import useQueryParam from './useQueryParam';
import { Vehicle } from '@sharedTypes/Vehicle';

type BranchDistance = {
  distance_to: number;
  name: string;
};

type BranchDistances = Record<string, BranchDistance>;

type ClickAndCollectData = {
  branchDistances: BranchDistances;
  clickAndCollectDetails: Vehicle['clickAndCollectDetails'];
} | null;

const CACHE_CAP = 10;

export const useClickAndCollect = () => {
  const [clickAndCollectData, setClickAndCollectData] =
    useState<ClickAndCollectData>(null);

  const cacheRef = useRef<Map<string, ClickAndCollectData>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [postcodeParam] = useQueryParam('location');
  const [showClickAndCollectOptionsParam] = useQueryParam(
    'show_click_and_collect_options'
  );

  const fetchClickAndCollectData = async () => {
    setIsLoading(true);

    const queryParams = new URLSearchParams({
      near: postcodeParam,
    });

    if (cacheRef.current.has(postcodeParam)) {
      setClickAndCollectData(cacheRef.current.get(postcodeParam) || null);
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `/api/click_and_collect/nearest_with_distances?${queryParams}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch: response is not OK');
      }

      const data: ClickAndCollectData = await response.json();

      if (cacheRef.current.size >= CACHE_CAP) {
        const oldestKey = cacheRef.current.keys().next().value;
        if (oldestKey !== undefined) {
          cacheRef.current.delete(oldestKey);
        }
      }
      cacheRef.current.set(postcodeParam, data);

      setClickAndCollectData(data);
    } catch (error) {
      setClickAndCollectData(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (showClickAndCollectOptionsParam && postcodeParam) {
      fetchClickAndCollectData();
    } else {
      setClickAndCollectData(null);
    }
  }, [showClickAndCollectOptionsParam, postcodeParam]);

  return { clickAndCollectData, isLoading };
};
